import { useState, useEffect } from 'react'
import moment from 'moment'
import { IsGroupWorkoutLocked } from 'functions/workoutUtils'

import KinesisWorkoutCalendarItem from 'components/Training/TrainingCalendar/KinesisWorkoutCalendarItem'
import KinesisEventCalendarItem from 'components/Training/TrainingCalendar/KinesisEventCalendarItem'
import TrainingPeaksWorkoutCalendarItem from 'components/Training/TrainingCalendar/TrainingPeaksWorkoutCalendarItem'

export default function TrainingCalendarDay({ 
    propProfileType = 'individual',
    kinesisUserGroupId = null,
    userId = null,
    kinesisProfileDetails = null,
    clientDate,
    timescale,
    date,
    isDateBeforeClientISOWeek,
    isDateInClientISOWeek,
    isDateAfterClientISOWeek,
    trainingCalendarData,
    exerciseHashMap = {},
    draggedKinesisWorkout = null,
    setDraggedKinesisWorkout = () => {},
    UpdateKinesisWorkoutCalendarItemDate = null,
    OnWorkoutOneOffOverridesSaved = () => {}
}) {

    const [isDragOver, setIsDragOver] = useState(false)

    const isDateBeforeClientDate = date.isBefore(clientDate)
    const isDateClientDate = date.format('YYYY-MM-DD') === clientDate
    const isDateAfterClientDate = date.isAfter(clientDate)

    let kinesisWorkoutCalendarItems = []
    let kinesisGroupIndividualWorkoutCalendarItems = []

    if (propProfileType === 'group') {

        kinesisWorkoutCalendarItems = (
            trainingCalendarData === null ? [] : 
                trainingCalendarData.filter((item) => (
                    item.kinesisUserGroupId !== null
                    && item.userId === null
                    && item.date === date.format('YYYY-MM-DD') 
                    && item.calendarEventType === 'kinesis-workout'
                ))
        )

        kinesisGroupIndividualWorkoutCalendarItems = (
            trainingCalendarData === null ? [] : 
                trainingCalendarData.filter((item) => (
                    item.kinesisUserGroupId !== null
                    && item.userId !== null
                    && item.date === date.format('YYYY-MM-DD') 
                    && item.calendarEventType === 'kinesis-workout'
                ))
        )

    }

    if (propProfileType === 'individual') {
        kinesisWorkoutCalendarItems = (
            trainingCalendarData === null ? [] : 
                trainingCalendarData.filter((item) => (
                    item.date === date.format('YYYY-MM-DD') 
                    && item.calendarEventType === 'kinesis-workout'
                ))
        )
    }

    const kinesisEventCalendarItems = (
        trainingCalendarData === null ? [] : 
            trainingCalendarData.filter((item) => (
                item.date === date.format('YYYY-MM-DD') 
                && item.calendarEventType === 'kinesis-event'
            ))
    )

    const trainingPeaksWorkoutCalendarItems = (
        trainingCalendarData === null ? [] : 
            trainingCalendarData.filter((item) => (
                item.calendarEventType === 'training-peaks-workout'
                && moment(item.WorkoutDay).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
                && item.Hidden === false
            ))
    )

    // Drag and drop. Workouts can only be moved to another day within their current ISO week

    const isKinesisWorkoutDraggable = (kinesisWorkout) => (
        UpdateKinesisWorkoutCalendarItemDate !== null
        && isDateBeforeClientISOWeek === false
        && kinesisWorkout.completed !== 1
        && (propProfileType !== 'group' || IsGroupWorkoutLocked(kinesisWorkout, kinesisGroupIndividualWorkoutCalendarItems) === false) // The group's copies stay on the same day
    )

    const isDragging = draggedKinesisWorkout !== null

    const isValidDropTarget = isDragging && date.isSame(moment(draggedKinesisWorkout.date, 'YYYY-MM-DD'), 'isoWeek')

    useEffect(() => { if (!isDragging) setIsDragOver(false) }, [isDragging]) // Clear highlight if a drag is cancelled over this day

    const OnDragOver = (e) => {
        if (!isValidDropTarget) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (!isDragOver) setIsDragOver(true)
    }

    const OnDragLeave = (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return
        setIsDragOver(false)
    }

    const OnDrop = (e) => {
        e.preventDefault()
        setIsDragOver(false)
        if (!isValidDropTarget) return
        UpdateKinesisWorkoutCalendarItemDate(draggedKinesisWorkout, date)
        setDraggedKinesisWorkout(null)
    }

    const dropTargetClassName = `
        ${isDragging && !isValidDropTarget ? 'opacity-40' : ''}
        ${isDragOver && isValidDropTarget ? 'ring-2 ring-inset ring-orange-400' : ''}
    `

    const dropTargetProps = {
        onDragOver: OnDragOver,
        onDragLeave: OnDragLeave,
        onDrop: OnDrop
    }

    const GetKinesisWorkoutDragProps = (kinesisWorkout) => ({
        isDraggable: isKinesisWorkoutDraggable(kinesisWorkout),
        isBeingDragged: draggedKinesisWorkout === kinesisWorkout,
        OnDragStart: () => setDraggedKinesisWorkout(kinesisWorkout),
        OnDragEnd: () => setDraggedKinesisWorkout(null)
    })

    if (Object.keys(exerciseHashMap).length === 0) {
        return null
    }

    if (timescale === 'block') {
        return (
            <div 
                className={`
                    flex h-full min-h-0 min-w-0 flex-col
                    ${isDateBeforeClientDate ? 'bg-gray-50' : ''}
                    ${isDateClientDate ? 'bg-orange-50' : ''}
                    ${isDateAfterClientDate ? 'bg-white' : ''}
                    ${dropTargetClassName}
                `}
                {...dropTargetProps}
            >
                <div className='flex items-center'>
                    {/* 
                    <div className="shrink-0 text-gray-600 px-1.5 pt-1 2xl:px-2 2xl:pt-1.5 text-xxs 2xl:text-xs text-left flex-1">
                        {date.format('ddd')}
                    </div> 
                    */}

                    <div className="shrink-0 text-gray-500 px-1.5 2xl:px-2 py-1 2xl:py-1.5 text-xxs 2xl:text-xs">
                        {date.format('MMM Do')}
                    </div>
                </div>

                {trainingCalendarData === null &&
                    <div className="flex-1 space-y-1 px-1">
                        {Array.from({ length: 2 }, (_, i) => (
                            <div
                                key={i}
                                className="h-6 rounded bg-gray-100 animate-pulse"
                            />
                        ))}
                    </div>
                }

                {trainingCalendarData !== null &&
                    <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1">
                        {kinesisEventCalendarItems.map((kinesisEvent) => (
                            <KinesisEventCalendarItem
                                key={kinesisEvent.id}
                                kinesisEvent={kinesisEvent}
                                timescale={timescale}
                            />
                        ))}

                        {kinesisWorkoutCalendarItems
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((data, index) => (
                            <KinesisWorkoutCalendarItem 
                                key={index}
                                propProfileType={propProfileType}
                                kinesisUserGroupId={kinesisUserGroupId}
                                userId={userId}
                                kinesisProfileDetails={kinesisProfileDetails}
                                kinesisGroupIndividualWorkoutCalendarItemData={kinesisGroupIndividualWorkoutCalendarItems}
                                timescale={timescale}
                                isDateBeforeClientISOWeek={isDateBeforeClientISOWeek}
                                isDateInClientISOWeek={isDateInClientISOWeek}
                                isDateAfterClientISOWeek={isDateAfterClientISOWeek}
                                isDateBeforeClientDate={isDateBeforeClientDate}
                                isDateClientDate={isDateClientDate}
                                isDateAfterClientDate={isDateAfterClientDate}
                                kinesisWorkoutCalendarItemData={data}
                                clientDate={clientDate}
                                OnWorkoutOneOffOverridesSaved={OnWorkoutOneOffOverridesSaved}
                                displayStatusText={false}
                                displayWorkoutStructure={false}
                                {...GetKinesisWorkoutDragProps(data)}
                            />
                        ))}

                        {trainingPeaksWorkoutCalendarItems.map((trainingPeaksWorkout) => (
                            <TrainingPeaksWorkoutCalendarItem
                                key={trainingPeaksWorkout.Id}
                                timescale={timescale}
                                trainingPeaksWorkout={trainingPeaksWorkout}
                            />
                        ))}
                    </div>
                }
            </div>
        )
    }

    if (timescale === 'week') {
        return (
            <div 
                className={`
                    relative flex h-full min-h-0 min-w-0 flex-col
                    ${isDateBeforeClientDate ? 'bg-gray-50' : ''}
                    ${isDateAfterClientDate ? 'bg-white' : ''}
                    ${isDateClientDate ? 'bg-orange-50' : ''}
                    ${dropTargetClassName}
                `}
                {...dropTargetProps}
            >
                <div
                    className={`
                        relative shrink-0 px-2 py-2 text-center min-w-0 border-b border-gray-200
                        ${isDateBeforeClientDate ? 'bg-gray-50' : ''}
                        ${isDateClientDate ? 'bg-orange-50' : ''}
                        ${isDateAfterClientDate ? 'bg-white' : ''}
                    `}
                >
                    <div className='truncate text-center tracking-wide font-bold text-xs 2xl:text-sm'>
                        {date.format('dddd')}
                    </div>
                </div>

                <div className="shrink-0 text-gray-600 px-1.5 2xl:px-2 pt-1 2xl:pt-1.5 text-xxs 2xl:text-xs">
                    {date.format('MMM Do')}
                </div>

                {trainingCalendarData === null && 
                    <div className="p-2 space-y-2">
                        {Array.from({ length: 4 }, (_, i) => (
                            <div 
                                key={i}
                                className="space-y-2 rounded-lg bg-gray-100 p-2 animate-pulse"
                            >
                                <div className="h-4" />
                            </div>
                        ))}
                    </div>
                }

                {trainingCalendarData !== null &&
                    <div 
                        className={`
                            flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2
                            ${isDateBeforeClientDate ? 'bg-gray-50' : ''}
                            ${isDateClientDate ? 'bg-orange-50' : ''}
                            ${isDateAfterClientDate ? 'bg-white' : ''}
                        `}
                    >
                        {kinesisEventCalendarItems.map((kinesisEvent) => (
                            <KinesisEventCalendarItem
                                key={kinesisEvent.id}
                                kinesisEvent={kinesisEvent}
                                timescale={timescale}
                            />
                        ))}

                        {kinesisWorkoutCalendarItems
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((data, index) => (
                            <KinesisWorkoutCalendarItem 
                                key={index}
                                propProfileType={propProfileType}
                                kinesisUserGroupId={kinesisUserGroupId}
                                userId={userId}
                                kinesisProfileDetails={kinesisProfileDetails}
                                kinesisGroupIndividualWorkoutCalendarItemData={kinesisGroupIndividualWorkoutCalendarItems}
                                timescale={timescale}
                                isDateBeforeClientISOWeek={isDateBeforeClientISOWeek}
                                isDateInClientISOWeek={isDateInClientISOWeek}
                                isDateAfterClientISOWeek={isDateAfterClientISOWeek}
                                isDateBeforeClientDate={isDateBeforeClientDate}
                                isDateClientDate={isDateClientDate}
                                isDateAfterClientDate={isDateAfterClientDate}
                                kinesisWorkoutCalendarItemData={data}
                                clientDate={clientDate}
                                OnWorkoutOneOffOverridesSaved={OnWorkoutOneOffOverridesSaved}
                                displayStatusText={false}
                                displayWorkoutStructure={true}
                                {...GetKinesisWorkoutDragProps(data)}
                            />
                        ))}

                        {trainingPeaksWorkoutCalendarItems.map((trainingPeaksWorkout) => (
                            <TrainingPeaksWorkoutCalendarItem
                                key={trainingPeaksWorkout.Id}
                                timescale={timescale}
                                trainingPeaksWorkout={trainingPeaksWorkout}
                            />
                        ))}
                    </div> 
                }

                {/* The white weekday header and the scrolling content paint over the inset ring on this element, so draw the drop highlight on top of them */}
                {isDragOver && isValidDropTarget && (
                    <div className='pointer-events-none absolute inset-0 z-10 ring-2 ring-inset ring-orange-400' />
                )}
            </div>
        )
    }

    return null
}
